import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, BadgeCheck, Calculator, CheckCircle2, Download, FileText, Loader2, Scale, UserPlus } from "lucide-react";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DisclosureFooter } from "@/components/FundingDisclosure";
import { useTour } from "@/components/tour/TourProvider";
import { LangToggle } from "@/pages/Legal";
import { api, formatErr } from "@/lib/api";
import { STATES } from "@/lib/constants";
import { PARTNER_TEXT, PARTNER_ERR_ES } from "@/lib/partners_text";
import { usePageMeta } from "@/lib/seo";

const PARTNER_META = {
  title: "Become a GrantVerify Partner | Flat-Fee Work for Grant Editors, CPAs & Attorneys",
  description: "Certified grant editors, CPAs and business attorneys: join the Peer Verify LLC Pro-Network and get paid a flat fee per completed grant review, with monthly payouts and no client billing.",
  path: "/partners",
};
const PARTNER_META_ES = {
  title: "Sea socio de GrantVerify | Trabajo con tarifa fija para editores, contadores y abogados",
  description: "Editores de subvenciones certificados, contadores (CPA) y abogados de negocios: únase a la Red Pro de Peer Verify LLC y reciba una tarifa fija por cada revisión completada, con pagos mensuales.",
  path: "/partners", image: "og-image-es.png",
};

const ICON = { editor: FileText, cpa: Calculator, attorney: Scale };
const EMPTY = { name: "", email: "", password: "", phone: "", pro_type: "", credential: "", state: "", years_experience: "", website: "", bio: "", signature: "" };

function RateCards({ types, pick, active, T, es }) {
  return (
    <div className="grid md:grid-cols-3 gap-4">
      {types.map((t) => {
        const I = ICON[t.key];
        return (
          <button type="button" key={t.key} onClick={() => pick(t.key)} data-testid={`partner-type-${t.key}`}
            className={`text-left rounded-lg border p-6 transition-colors ${active === t.key ? "border-emerald-500 bg-emerald-50/50 ring-1 ring-emerald-500" : "border-slate-200 bg-white hover:border-slate-400"}`}>
            <I className="h-6 w-6 text-emerald-600" />
            <div className="font-display text-lg font-bold text-slate-900 mt-4">{es ? t.label_es : t.label}</div>
            <div className="mt-2 flex items-baseline gap-1"><span className="font-display text-4xl font-extrabold text-slate-900" data-testid={`partner-rate-${t.key}`}>${t.rate}</span><span className="text-sm text-slate-500">{T.perJob}</span></div>
            <p className="text-sm text-slate-600 mt-3 leading-relaxed">{T.work[t.key]}</p>
          </button>
        );
      })}
    </div>
  );
}

function Field({ id, label, children }) {
  return <div><Label htmlFor={id}>{label}</Label><div className="mt-1.5">{children}</div></div>;
}

function DetailsCard({ f, set, setF, T }) {
  const F = T.fields;
  return (
    <div className="lg:col-span-7 bg-white border border-slate-200 rounded-lg p-6 space-y-4">
      <h2 className="font-display text-2xl font-bold text-slate-900">{T.details}</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field id="p-name" label={F.name}><Input id="p-name" required value={f.name} onChange={set("name")} data-testid="partner-name-input" /></Field>
        <Field id="p-email" label={F.email}><Input id="p-email" type="email" required value={f.email} onChange={set("email")} data-testid="partner-email-input" /></Field>
        <Field id="p-pass" label={F.password}><Input id="p-pass" type="password" required minLength={8} value={f.password} onChange={set("password")} data-testid="partner-password-input" /></Field>
        <Field id="p-phone" label={F.phone}><Input id="p-phone" required value={f.phone} onChange={set("phone")} data-testid="partner-phone-input" /></Field>
        <Field id="p-cred" label={F.credential}><Input id="p-cred" required value={f.credential} onChange={set("credential")} data-testid="partner-credential-input" /></Field>
        <Field id="p-state" label={F.state}>
          <Select value={f.state} onValueChange={(v) => setF({ ...f, state: v })}>
            <SelectTrigger id="p-state" data-testid="partner-state-select"><SelectValue placeholder={T.statePh} /></SelectTrigger>
            <SelectContent className="max-h-72">{STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field id="p-years" label={F.years}><Input id="p-years" type="number" min={0} max={60} required value={f.years_experience} onChange={set("years_experience")} data-testid="partner-years-input" /></Field>
        <Field id="p-web" label={F.website}><Input id="p-web" value={f.website} onChange={set("website")} data-testid="partner-website-input" /></Field>
      </div>
      <Field id="p-bio" label={F.bio}><Textarea id="p-bio" rows={4} required minLength={20} value={f.bio} onChange={set("bio")} placeholder={T.bioPh} data-testid="partner-bio-input" /></Field>
    </div>
  );
}

function AgreementCard({ terms, es, rate, agreed, setAgreed, f, set, err, busy, T }) {
  return (
    <div className="lg:col-span-5 bg-slate-900 text-white rounded-lg p-6 flex flex-col" data-testid="partner-agreement">
      <h2 className="font-display text-2xl font-bold flex items-center gap-2"><BadgeCheck className="h-5 w-5 text-emerald-400" />{T.agreementTitle}</h2>
      <p className="text-sm text-slate-300 mt-2">{T.feeFor} <b className="text-white" data-testid="partner-agreement-rate">{rate ? `$${rate} ${T.perJob}` : T.feeNone}</b></p>
      <ol className="mt-4 space-y-2.5 text-sm text-slate-300 list-decimal pl-5 flex-1" data-testid="partner-agreement-terms">{(es ? terms.agreement_es : terms.agreement).map((l) => <li key={l}>{l}</li>)}</ol>
      {es && <p className="text-xs text-slate-400 italic mt-3" data-testid="partner-es-governs">{terms.es_governs}</p>}
      <label className="flex gap-2.5 items-start text-sm mt-5 cursor-pointer" data-testid="partner-agree-label">
        <Checkbox checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} className="mt-0.5 border-slate-400" data-testid="partner-agree-checkbox" />
        <span>{T.agree[0]} <Link to="/terms" target="_blank" className="text-emerald-400 hover:underline">{T.agree[1]}</Link> (<Link to="/contractor-agreement" target="_blank" className="text-emerald-400 hover:underline" data-testid="partner-contractor-link">{es ? "acuerdo completo" : "full agreement"}</Link>).</span>
      </label>
      <Field id="p-sign" label={<span className="text-slate-300">{T.sign}</span>}><Input id="p-sign" required value={f.signature} onChange={set("signature")} className="bg-slate-800 border-slate-700 text-white font-display italic text-lg" data-testid="partner-signature-input" /></Field>
      {err && <p className="text-sm text-red-400 mt-3" data-testid="partner-apply-error">{err}</p>}
      <Button type="submit" disabled={busy || !agreed} className="mt-4 h-11 bg-emerald-500 hover:bg-emerald-400 text-slate-950" data-testid="partner-apply-submit">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{T.submit}</Button>
    </div>
  );
}

export default function Partners() {
  const [params] = useSearchParams();
  const ref = params.get("ref") || "";
  const { lang } = useTour();
  const es = lang === "es";
  const T = PARTNER_TEXT[es ? "es" : "en"];
  const [terms, setTerms] = useState(null);
  const [inviter, setInviter] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [loadErr, setLoadErr] = useState(false);
  const [done, setDone] = useState(null);
  usePageMeta(es ? PARTNER_META_ES : PARTNER_META);
  useEffect(() => { api.get("/partners/terms").then((r) => setTerms(r.data)).catch(() => setLoadErr(true)); }, []);
  useEffect(() => { if (ref) api.get(`/partners/ref/${encodeURIComponent(ref)}`).then((r) => setInviter(r.data.name)).catch(() => {}); }, [ref]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const rate = terms?.types.find((t) => t.key === f.pro_type)?.rate;
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!f.pro_type) return setErr(T.chooseErr);
    if (!f.state) return setErr(T.stateErr);
    setBusy(true);
    try { const { data } = await api.post("/partners/apply", { ...f, years_experience: Number(f.years_experience), agreed, lang: es ? "es" : "en", ref }); setDone(data.rate); window.scrollTo(0, 0); }
    catch (e2) { const m = formatErr(e2); setErr(es ? PARTNER_ERR_ES[m] || (e2?.response?.status === 422 ? "Revise los datos: correo válido, contraseña de 8+ caracteres y resumen de 20+ caracteres." : m) : m); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="partners-page" data-no-translate lang={es ? "es" : "en"}>
      <header className="bg-white border-b border-slate-200"><div className="max-w-5xl mx-auto px-6 py-5 flex justify-between items-center gap-4"><Brand /><div className="flex items-center gap-3 sm:gap-5"><LangToggle /><Link to="/login" className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1" data-testid="partners-login-link"><ArrowLeft className="h-4 w-4" /><span className="hidden sm:inline">{T.signIn}</span></Link></div></div></header>
      <main className="max-w-5xl mx-auto px-6 py-16 w-full flex-1">
        {inviter && <div className="mb-6 inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm rounded-full px-4 py-1.5" data-testid="partner-invited-banner"><UserPlus className="h-4 w-4" />{inviter} {T.invited}</div>}
        <div className="eyebrow mb-3">{T.eyebrow}</div>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 max-w-3xl" data-testid="partners-title">{T.title}</h1>
        <p className="text-slate-600 mt-4 max-w-2xl text-lg">{T.sub}</p>
        <a href={`/brand/guides/grantverify-partner-brief-${es ? "es" : "en"}.pdf`} download className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-emerald-700 hover:underline underline-offset-4" data-testid="partners-download-brief-link"><Download className="h-4 w-4" />{es ? "Descargue el resumen para socios (PDF)" : "Download the partner brief (PDF)"}</a>
        <ol className="mt-10 grid md:grid-cols-4 gap-4">
          {T.steps.map((s, i) => <li key={s} className="bg-white border border-slate-200 rounded-lg p-4 text-sm text-slate-700"><span className="font-mono text-xs text-emerald-600 block mb-1">{String(i + 1).padStart(2, "0")}</span>{s}</li>)}
        </ol>
        <h2 className="font-display text-2xl font-bold text-slate-900 mt-14 mb-4">{T.chooseRole}</h2>
        {!terms ? (loadErr ? <p className="text-sm text-red-600" data-testid="partner-terms-error">{T.loadErr}</p> : <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />) : <RateCards types={terms.types} pick={(k) => setF({ ...f, pro_type: k })} active={f.pro_type} T={T} es={es} />}
        <div className="mt-14">
          {done != null ? (
            <div className="bg-white border border-emerald-300 rounded-lg p-10 text-center" data-testid="partner-apply-success">
              <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto" />
              <h2 className="font-display text-2xl font-extrabold text-slate-900 mt-4">{T.doneTitle}</h2>
              <p className="text-slate-600 mt-2 max-w-md mx-auto">{T.done(done)}</p>
            </div>
          ) : terms && (
            <form onSubmit={submit} className="grid lg:grid-cols-12 gap-6" data-testid="partner-apply-form">
              <DetailsCard f={f} set={set} setF={setF} T={T} />
              <AgreementCard terms={terms} es={es} rate={rate} agreed={agreed} setAgreed={setAgreed} f={f} set={set} err={err} busy={busy} T={T} />
            </form>
          )}
        </div>
      </main>
      <DisclosureFooter />
    </div>
  );
}
